/**
 * forensicsPanel.ts — the shared death-forensics panel builder (SURF-02, 272-UI-SPEC.md).
 *
 * The ONE module both `feed.ts`'s JS-built Activity Feed stream row and `profile.ts`'s Graves
 * accordion use to lazily fetch and render a single death's forensics body. Standing alone rather
 * than living inside `feed.ts` keeps `profile.ts` from having to import `feed.ts`, and makes the
 * item tile's data contract (see rule 2 below) an explicit, single-file surface reviewable against
 * `player.latte`'s own `{define mcItemTile}`.
 *
 * Two rules govern every line of this file:
 *
 * 1. Every payload-derived string is written via `textContent`/`createElement` — NEVER
 *    `innerHTML`. This docblock line is REQUIRED and must NOT be deleted or reworded to dodge a
 *    grep — every `innerHTML` gate this plan writes is comment-filtered precisely so this line can
 *    stay. If a gate ever reads non-zero, the defect is in executable code, never in this
 *    docblock. Enforced by literal grep (T-231-07/T-232-03), matching `feed.ts:11`'s identical
 *    rule — the forensics panel is the densest DOM this codebase builds client-side, exactly why
 *    the temptation must be named here.
 * 2. This module is the TypeScript half of a SHARED DATA CONTRACT with `player.latte`'s
 *    `{define mcItemTile}`. Latte `{define}` blocks compile to PHP and the browser never sees
 *    them, so the tile cannot be shared as source — only as a fixed JSON field set
 *    (`namespaced_item_id`, `humanized_name`, `icon_url`, `icon_fallback_url`, `enchant_display`,
 *    `count`, `slot`) plus a fixed set of CSS class names (`mc-item-tile`, `mc-item-tile-text`,
 *    `mc-item-tile-label`, `mc-item-enchants`, `mc-item-count`, `mc-item-slot`,
 *    `mc-item-group`, `mc-item-group-heading`, `mc-item-group-list`, `mc-item-row`). Both halves
 *    are named explicitly here so a future reader knows to change them together — a change on one
 *    side does not propagate to the other automatically.
 *
 * Imports nothing from `./feed` and is imported by nothing there yet — `feed.ts`/`profile.ts`
 * wiring is a later plan (272-12). Must stay importable by both with no import cycle.
 */

import { buildCoordPill } from './coordPill';

// ---------------------------------------------------------------------------
// Payload types — App\Models\McFeedModel::getDeathForensics()'s return shape
// ---------------------------------------------------------------------------

/** One enriched item, mirroring `App\Mc\McItemTile::enrichOne()`'s fixed return shape — the
 *  data-contract half shared with `player.latte`'s `{define mcItemTile}`. `slot` is always a raw,
 *  verbatim string (never humanized, never null) per D-16 — the enrichment helper defaults an
 *  absent wire `slot` to `''`, it never omits the key. */
export interface ForensicsItem {
    namespaced_item_id: string;
    humanized_name: string;
    count: number;
    /** Raw, verbatim slot token. D-16: zero interpretation — never guess which inventory
     *  position `vanilla_0..8` maps to, never prettify PC-8's accessory taxonomy. */
    slot: string;
    icon_url: string;
    icon_fallback_url: string | null;
    enchant_display: string;
}

/**
 * `App\Models\McFeedModel::getDeathForensics()`'s full success payload. Fields present on every
 * real response today are left required; the four `*_display_name` attribution fields and the
 * coordinate/damage-type fields are marked optional (in addition to already being nullable) so a
 * response from a deploy-window-mismatched backend degrades to "field absent" rather than a thrown
 * property-access error — the same defensive convention `StreamRow`'s own optional fields document
 * in `feed.ts`.
 */
export interface ForensicsPayload {
    death_event_id: string;
    /** The DEATH's own occurred_at (naive-UTC string) — never the forensics row's own capture
     *  time (PC-4/D-25 trap). This panel does not render it; callers already have the death's own
     *  timestamp from the row/grave that triggered the expand. */
    death_occurred_at: string;
    vanilla_items: ForensicsItem[];
    accessory_items: ForensicsItem[];
    /** Raw double, PRE-rounded server-side to 2 decimal places. D-14: the panel rounds this
     *  further to a whole number for DISPLAY ONLY — PC-6's absurd raw precision must never reach
     *  the DOM at any fractional precision. Never subtracted from `xp_stored`. */
    xp_original: number;
    /** Raw int. D-14: never subtracted from `xp_original` — no derived-deficit figure anywhere. */
    xp_stored: number;
    /** The verbatim, normalized status token (`claimed`/`unclaimed`/`destroyed`/an unrecognized
     *  lowercased pass-through/`unknown`) — `App\Mc\DeathForensics::badgeLabel()`'s output. */
    status: string;
    /** A bare Bootstrap variant token — validated against a fixed allow-list before it ever
     *  reaches a class name (T-272-19); never trusted verbatim. */
    status_variant: string;
    /** The grave record's OWN raw, non-namespaced reading (e.g. `"lava"`) — D-15: never compared
     *  or equated to the death event's own `cause`. */
    damage_type_id?: string | null;
    grave_id?: string | null;
    killed_display_name?: string | null;
    source_display_name?: string | null;
    attacker_display_name?: string | null;
    prime_adversary_display_name?: string | null;
    bluemap_href?: string | null;
    dimension?: string | null;
    pos?: number[] | null;
}

/**
 * The honest "no forensics for this id" response — `McPresenter::handleGetDeathForensics()`
 * answers a well-formed but out-of-scope/nonexistent death id with this shape at HTTP 200, never a
 * 404. In practice this row should never be reachable in the UI (the expand affordance only
 * renders when `has_forensics` is already true), but the client must still handle it honestly
 * rather than assume the flag was correct.
 */
interface ForensicsAbsentPayload {
    has_forensics: false;
}

type ForensicsResponse = ForensicsPayload | ForensicsAbsentPayload;

function isAbsentPayload(value: unknown): value is ForensicsAbsentPayload {
    return (
        typeof value === 'object'
        && value !== null
        && (value as { has_forensics?: unknown }).has_forensics === false
    );
}

// ---------------------------------------------------------------------------
// Copy constants — shared verbatim by every consumer (272-UI-SPEC.md's Copywriting Contract)
// ---------------------------------------------------------------------------

export const FORENSICS_LOADING_TEXT = 'Loading forensics…';
export const FORENSICS_ERROR_TEXT
    = "Forensics couldn't be loaded right now. Try expanding this row again.";

// ---------------------------------------------------------------------------
// Per-id response cache + in-flight dedup + per-id stale-response guard (D-11, T-272-20)
// ---------------------------------------------------------------------------

/** Keyed by death event id. A cache HIT (including a cached `null`) returns immediately with NO
 *  network call — D-11 requires that a second expand of the same row never re-fetches. */
const responseCache = new Map<string, ForensicsPayload | null>();

/** Keyed by death event id. Two rapid expands of the SAME row before the first response lands
 *  await the SAME promise rather than issuing a second request. */
const inFlight = new Map<string, Promise<ForensicsPayload | null>>();

/**
 * Keyed by death event id, mirroring `profile.ts`'s `fetchSessions()`/`sessionRequestSeq` guard —
 * cloned per-id here rather than as one module-level counter, so a slow response for one row's
 * death id can never overwrite a DIFFERENT row's already-newer render. Bumped by `renderInto()`
 * (below) on every call; a render whose captured sequence number no longer matches the current one
 * for that id knows a newer expand of the SAME row has since superseded it and must not write the
 * DOM.
 */
const requestSeq = new Map<string, number>();

function bumpRequestSeq(deathEventId: string): number {
    const seq = (requestSeq.get(deathEventId) ?? 0) + 1;
    requestSeq.set(deathEventId, seq);
    return seq;
}

function currentRequestSeq(deathEventId: string): number {
    return requestSeq.get(deathEventId) ?? 0;
}

/**
 * Fetches (or returns the cached) forensics body for one death event id. Cached per-id
 * indefinitely for the page's lifetime — D-11's "a second expand must not re-fetch" rule.
 *
 * `null` covers three cases uniformly: a non-ok HTTP response, a network/parse error, and the
 * honest `has_forensics:false` response — all three are cached, so a `null` result is also never
 * re-fetched on a second expand.
 */
export async function fetchForensics(deathEventId: string): Promise<ForensicsPayload | null> {
    if (responseCache.has(deathEventId)) {
        return responseCache.get(deathEventId) ?? null;
    }

    const pending = inFlight.get(deathEventId);
    if (pending) {
        return pending;
    }

    const promise = (async (): Promise<ForensicsPayload | null> => {
        const url = `?do=getDeathForensics&id=${encodeURIComponent(deathEventId)}`;
        try {
            const resp = await fetch(url, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            if (!resp.ok) {
                console.error('[mc-forensics] Forensics fetch failed:', resp.status);
                return null;
            }

            const raw: ForensicsResponse = await resp.json();
            if (isAbsentPayload(raw)) {
                return null;
            }

            return raw;
        } catch (err) {
            console.error('[mc-forensics] Forensics fetch error:', err);
            return null;
        }
    })();

    inFlight.set(deathEventId, promise);

    try {
        const result = await promise;
        responseCache.set(deathEventId, result);
        return result;
    } finally {
        inFlight.delete(deathEventId);
    }
}

// ---------------------------------------------------------------------------
// Item tile builder — the PARALLEL TS half of the {define mcItemTile}/{define mcItemIcon} data
// contract declared in `player.latte` (D-08's shared markup, extracted for the SSR side; this
// function is the TS side, since Latte {define} blocks compile to PHP and the browser never sees
// them — there is no way for client-side JS to {include} that markup).
//
// Source this mirrors verbatim (app/Presenters/templates/Mc/player.latte, `{define mcItemIcon}` /
// `{define mcItemTile}`):
//
//   {define mcItemIcon, array $item}
//   <img src="{$item['icon_url']}" alt="" loading="lazy"
//   {if ($item['icon_fallback_url'] ?? null) !== null}
//   onerror="this.onerror=()=>this.style.visibility='hidden';this.src={$item['icon_fallback_url']}"
//   {else}
//   onerror="this.style.visibility='hidden'"
//   {/if}>
//   {/define}
//
//   {define mcItemTile, array $item, ?string $slotLabel = null}
//   <span class="mc-item-tile">
//       {include mcItemIcon, $item}
//       <span class="mc-item-tile-text">
//           <span class="mc-item-tile-label">{$item['humanized_name']}</span>
//           {if !empty($item['enchant_display'])}<span class="mc-item-enchants">{$item['enchant_display']}</span>{/if}
//       </span>
//   </span>
//   {if $item['count'] > 1}<span class="mc-item-count">&times;{$item['count']|number}</span>{/if}
//   {if $slotLabel !== null}<span class="mc-item-slot text-muted small">{$slotLabel}</span>{/if}
//   {/define}
//
// ⚠ A change to EITHER side (the Latte define above, or this function) must be made on BOTH — this
// is a consciously accepted duplication (RESEARCH Pattern 4), verified independently on each side,
// never assumed to stay in lock-step automatically.
// ---------------------------------------------------------------------------

/**
 * Builds one `.mc-item-tile` + trailing `.mc-item-count`/`.mc-item-slot` siblings, matching
 * `{define mcItemTile}`'s exact DOM shape and class-name set. Unlike the Latte define (whose
 * `$slotLabel` is nullable and optional), a `ForensicsItem.slot` is always a non-null string
 * (`App\Mc\McItemTile::enrichOne()` defaults an absent wire slot to `''`, never omits the key), so
 * the `.mc-item-slot` span always renders here — no null guard needed.
 *
 * Every string assignment uses `textContent`; every attribute uses a typed DOM property or
 * `setAttribute`. No template literal here contains an HTML tag.
 */
function buildItemTile(item: ForensicsItem): DocumentFragment {
    const frag = document.createDocumentFragment();

    const tile = document.createElement('span');
    tile.className = 'mc-item-tile';

    // {include mcItemIcon, $item} — the Phase 245 D-03 three-tier onerror chain: pack texture ->
    // (if a fallback exists) stock vanilla icon -> hidden <img> with the name still rendered.
    // Attached as function properties, NEVER as a string of JavaScript (T-272-03).
    const img = document.createElement('img');
    img.alt = '';
    img.loading = 'lazy';
    img.src = item.icon_url;
    const fallbackUrl = item.icon_fallback_url;
    if (fallbackUrl !== null) {
        img.onerror = () => {
            img.onerror = () => { img.style.visibility = 'hidden'; };
            img.src = fallbackUrl;
        };
    } else {
        img.onerror = () => { img.style.visibility = 'hidden'; };
    }
    tile.appendChild(img);

    const tileText = document.createElement('span');
    tileText.className = 'mc-item-tile-text';

    const label = document.createElement('span');
    label.className = 'mc-item-tile-label';
    label.textContent = item.humanized_name;
    tileText.appendChild(label);

    if (item.enchant_display !== '') {
        const enchants = document.createElement('span');
        enchants.className = 'mc-item-enchants';
        enchants.textContent = item.enchant_display;
        tileText.appendChild(enchants);
    }

    tile.appendChild(tileText);
    frag.appendChild(tile);

    if (item.count > 1) {
        const count = document.createElement('span');
        count.className = 'mc-item-count';
        count.textContent = `×${item.count.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
        frag.appendChild(count);
    }

    // D-16: verbatim, zero slot-label interpretation — never guess which inventory position
    // vanilla_0..8 maps to, never prettify PC-8's accessory taxonomy, never strip a prefix.
    const slot = document.createElement('span');
    slot.className = 'mc-item-slot text-muted small';
    slot.textContent = item.slot;
    frag.appendChild(slot);

    return frag;
}

/**
 * Builds one .mc-item-group — an .mc-item-group-heading labelled group of .mc-item-row tiles,
 * matching player.latte's existing inventory group shape: a wrapper div, a heading element, and
 * an unstyled list of item rows. When `items` is empty, renders ONE muted "None" row rather than
 * omitting the group — D-16's two groups are always both labelled, so an operator can tell "no
 * accessories" from "accessories not captured". The empty-row markup deliberately reuses only the
 * already-allowed `mc-item-row`/`mc-item-tile`/`mc-item-tile-label` classes (no `--empty` modifier
 * class is introduced here).
 */
function buildItemGroup(headingText: string, items: ForensicsItem[]): HTMLElement {
    const group = document.createElement('div');
    group.className = 'mc-item-group';

    const heading = document.createElement('div');
    heading.className = 'mc-item-group-heading';
    heading.textContent = headingText;
    group.appendChild(heading);

    const list = document.createElement('ul');
    list.className = 'mc-item-group-list list-unstyled';
    group.appendChild(list);

    if (items.length === 0) {
        const row = document.createElement('li');
        row.className = 'mc-item-row';

        const tile = document.createElement('span');
        tile.className = 'mc-item-tile';

        const label = document.createElement('span');
        label.className = 'mc-item-tile-label text-muted';
        label.textContent = 'None';
        tile.appendChild(label);

        row.appendChild(tile);
        list.appendChild(row);
        return group;
    }

    for (const item of items) {
        const row = document.createElement('li');
        row.className = 'mc-item-row';
        row.appendChild(buildItemTile(item));
        list.appendChild(row);
    }

    return group;
}

// ---------------------------------------------------------------------------
// buildForensicsPanel() — the full expanded body (D-14..D-17)
// ---------------------------------------------------------------------------

/** The only Bootstrap badge variant tokens this panel ever composes into a class name — anything
 *  else falls back to `secondary` (T-272-19). The raw `status` token itself only ever reaches the
 *  DOM via `textContent`, never as part of a class name. */
const KNOWN_BADGE_VARIANTS = new Set(['success', 'warning', 'danger', 'secondary']);

function appendLabelledLine(parent: HTMLElement, label: string, value: string): void {
    const line = document.createElement('p');
    line.className = 'mb-1 small';

    const strong = document.createElement('span');
    strong.className = 'fw-semibold';
    strong.textContent = `${label}: `;
    line.appendChild(strong);

    line.appendChild(document.createTextNode(value));
    parent.appendChild(line);
}

function buildXpColumn(label: string, rawValue: number): HTMLElement {
    const col = document.createElement('div');
    col.className = 'col-6 col-sm-3';

    const lab = document.createElement('div');
    lab.className = 'text-muted small';
    lab.textContent = label;
    col.appendChild(lab);

    const val = document.createElement('div');
    val.className = 'fw-semibold';
    // Rounded to a WHOLE number for display only (D-14/PC-6) — the raw value is never printed at
    // full precision, and this figure is never combined with its sibling figure anywhere below.
    val.textContent = Math.round(rawValue).toLocaleString('en-US', { maximumFractionDigits: 0 });
    col.appendChild(val);

    return col;
}

/**
 * Builds the full expanded forensics body for one death. Content order follows 272-UI-SPEC.md's
 * focal-point ruling — the two item lists render FIRST, then the claim badge/caveat, then the XP
 * figures, then the damage type, then (when a coordinate resolves) a coordinate, then the
 * remaining identity fields. Every node is `createElement`/`textContent` only — zero `innerHTML`,
 * zero template literal containing an HTML tag, zero class name composed from an unvalidated
 * payload string.
 */
export function buildForensicsPanel(payload: ForensicsPayload): HTMLElement {
    const panel = document.createElement('div');

    // 1. Item lists (D-16, the focal point) — NEVER merged, NEVER concatenated.
    const vanillaItems = Array.isArray(payload.vanilla_items) ? payload.vanilla_items : [];
    const accessoryItems = Array.isArray(payload.accessory_items) ? payload.accessory_items : [];
    panel.appendChild(buildItemGroup('Items Recovered', vanillaItems));
    panel.appendChild(buildItemGroup('Accessories', accessoryItems));

    // 2. Claim badge + one-shot caveat (D-17), stated ONCE per panel, beside the badge.
    const badgeRow = document.createElement('div');
    badgeRow.className = 'd-flex align-items-start flex-wrap gap-2 mt-3 mb-3';

    const variant = KNOWN_BADGE_VARIANTS.has(payload.status_variant) ? payload.status_variant : 'secondary';
    const badge = document.createElement('span');
    badge.className = `badge text-bg-${variant}`;
    badge.textContent = payload.status;
    badgeRow.appendChild(badge);

    const caveat = document.createElement('span');
    caveat.className = 'text-muted small';
    caveat.textContent = 'Captured once, shortly after death — never updated. The grave may have been claimed since.';
    badgeRow.appendChild(caveat);

    panel.appendChild(badgeRow);

    // 3. XP figures (D-14) — two separately-labelled raw numbers. Compute NOTHING here: never
    // subtract `xp_stored` from `xp_original`, and never render any derived deficit figure under
    // any label — the exact YIGD semantics of these two fields are not established anywhere in
    // either repo, so a subtraction would print an invented number with the authority of a
    // measurement.
    const xpRow = document.createElement('div');
    xpRow.className = 'row g-2 mb-3';
    xpRow.appendChild(buildXpColumn('XP at death', payload.xp_original));
    xpRow.appendChild(buildXpColumn('XP kept', payload.xp_stored));
    panel.appendChild(xpRow);

    // 4. Damage type (D-15) — the grave record's OWN reading. Never compared or equated to the
    // feed row's own cause anywhere in this function — the two readings can and will legitimately
    // disagree on the same death.
    const damageWrap = document.createElement('div');
    damageWrap.className = 'mb-3';
    appendLabelledLine(damageWrap, 'Cause (grave record)', payload.damage_type_id ?? 'unknown');
    const damageNote = document.createElement('p');
    damageNote.className = 'text-muted small mb-0';
    damageNote.textContent
        = "This is the grave's own reading, not the death feed's cause — the two can legitimately disagree.";
    damageWrap.appendChild(damageNote);
    panel.appendChild(damageWrap);

    // 5. Coordinate (Claude's discretion, bounded by 254-UI-SPEC §9.1 Rule A). Reuses the
    // server-resolved `bluemap_href` for THIS SAME death — never a second, competing pill, and
    // never a client-side re-derivation of the dimension-to-map-id mapping.
    const pos = Array.isArray(payload.pos) && payload.pos.length === 3 ? payload.pos : null;
    if (pos !== null) {
        const coordWrap = document.createElement('div');
        coordWrap.className = 'mb-3';
        const href = payload.bluemap_href ?? null;
        if (href !== null) {
            coordWrap.appendChild(buildCoordPill({
                tag: 'a',
                x: pos[0],
                y: pos[1],
                z: pos[2],
                dimension: payload.dimension ?? '',
                href,
                showArrow: true,
            }));
        } else {
            // Rule A: no href resolves -> plain unstyled text, zero pill chrome, zero dimension
            // colour. The neutral tooltip-only fallback class is deliberately never applied here.
            coordWrap.appendChild(document.createTextNode(`${pos[0]}, ${pos[1]}, ${pos[2]}`));
        }
        panel.appendChild(coordWrap);
    }

    // 6. grave_id + the four *_display_name fields — rendered only when present/non-null, each
    // explicitly labelled. A null/absent field is simply OMITTED here — never rendered as an
    // empty string, never as 0 (270 D-19).
    const identityWrap = document.createElement('div');
    const identityFields: Array<[string, string | null | undefined]> = [
        ['Grave', payload.grave_id],
        ['Killed display name', payload.killed_display_name],
        ['Source display name', payload.source_display_name],
        ['Attacker display name', payload.attacker_display_name],
        ['Prime adversary display name', payload.prime_adversary_display_name],
    ];
    for (const [label, value] of identityFields) {
        if (value === null || value === undefined || value === '') {
            continue;
        }
        appendLabelledLine(identityWrap, label, value);
    }
    panel.appendChild(identityWrap);

    return panel;
}

// ---------------------------------------------------------------------------
// renderInto() — the AJAX-fills-a-server-rendered-shell convenience both consumers call
// ---------------------------------------------------------------------------

/**
 * Clears `container`, shows the loading copy, awaits `fetchForensics()`, then replaces the
 * container's children with `buildForensicsPanel()`'s output — or the error copy on a `null`
 * result. Guards against a stale response for this SAME death id overwriting a newer render of the
 * SAME container (e.g. a rapid collapse/re-expand) via the per-id sequence counter declared above.
 * Clearing always uses `replaceChildren()` — never a markup-string assignment of any kind.
 */
export async function renderInto(container: HTMLElement, deathEventId: string): Promise<void> {
    const seq = bumpRequestSeq(deathEventId);

    container.replaceChildren();
    const loading = document.createElement('p');
    loading.className = 'text-muted small mb-0';
    loading.textContent = FORENSICS_LOADING_TEXT;
    container.appendChild(loading);

    const payload = await fetchForensics(deathEventId);

    // A newer renderInto() call for this SAME death id has already superseded this one — never
    // write a stale result over a newer render.
    if (currentRequestSeq(deathEventId) !== seq) {
        return;
    }

    container.replaceChildren();

    if (payload === null) {
        const error = document.createElement('p');
        error.className = 'text-muted small mb-0';
        error.textContent = FORENSICS_ERROR_TEXT;
        container.appendChild(error);
        return;
    }

    container.appendChild(buildForensicsPanel(payload));
}
